import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  Step,
  Stepper,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useSnackbar } from '../contexts/SnackbarContext';
import { getProviderIcon, getStatusColor } from '../utils/documents/documentSourceHelpers';

import {
  useAvailableProviders,
  useUserConnections,
  useCreateUserConnection,
  useUpdateUserConnection,
  useDeleteUserConnection,
  useSyncConnection,
  useParseSharePointLink,
  useBrowseSharePointFiles,
  useSyncSelectedFiles,
} from '../hooks/api/useDocumentSources';
import { log } from '../utils/errors/logger';
import { formatDateTime } from '../utils/formatting/formatters';
import type {
  DocumentSourceUserConnectionRequest,
  DocumentSourceUserConnectionResponse,
} from '../api/data-contracts';

// Using types from generated API client instead of local interfaces

interface FileTreeNode {
  id: string;
  name: string;
  path: string;
  is_file: boolean;
  size?: number;
  modified_at?: string;
  mime_type?: string;
  selected: boolean;
  children: FileTreeNode[];
}

interface SharePointLinkInfo {
  tenant_domain: string;
  user_principal: string;
  folder_path: string;
  original_url: string;
  is_valid: boolean;
}

const DocumentSourceConnections: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();

  // TanStack Query hooks
  const { data: connections = [], isLoading: connectionsLoading, error: connectionsError } = useUserConnections();
  const { data: providerConfigs = [], isLoading: providersLoading } = useAvailableProviders();

  // Mutations
  const createConnection = useCreateUserConnection();
  const updateConnection = useUpdateUserConnection();
  const deleteConnection = useDeleteUserConnection();
  const syncConnection = useSyncConnection();
  const parseSharePointLink = useParseSharePointLink();
  const browseSharePointFiles = useBrowseSharePointFiles();
  const syncSelectedFiles = useSyncSelectedFiles();

  // Local state for UI
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<DocumentSourceUserConnectionResponse | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<DocumentSourceUserConnectionResponse | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [parsedLinkInfo, setParsedLinkInfo] = useState<SharePointLinkInfo | null>(null);
  const [fileTree, setFileTree] = useState<FileTreeNode | null>(null);
  const [showFileTree, setShowFileTree] = useState(false);

  // Form state
  const [formData, setFormData] = useState<DocumentSourceUserConnectionRequest>({
    provider_config_id: '',
    name: '',
    enabled: true,
    sync_interval: 'daily',
    file_filters: ['*.pdf', '*.docx', '*.txt', '*.pptx', '*.xlsx'],
    max_file_size: 50 * 1024 * 1024, // 50MB
    folder_path: '/',
    share_url: '',
  });

  // Derive isPublicLink from share_url to avoid undefined variable runtime crash
  const isPublicLink = formData.share_url && formData.share_url.includes('public');

  const steps = [
    t('documentSources.steps.basicConfiguration'),
    t('documentSources.steps.sharepointLink'),
    t('documentSources.steps.fileSelection')
  ];
  
  // Combined loading state
  const loading = connectionsLoading || providersLoading;

  const handleOpenDialog = (connection?: DocumentSourceUserConnectionResponse) => {
    if (connection) {
      setEditingConnection(connection);
      setFormData({
        provider_config_id: connection.provider_config_id,
        name: connection.name,
        enabled: connection.enabled,
        sync_interval: connection.sync_interval,
        file_filters: connection.file_filters,
        max_file_size: connection.max_file_size,
        folder_path: connection.folder_path,
        share_url: connection.share_url || '',
      });
    } else {
      setEditingConnection(null);
      setFormData({
        provider_config_id: '',
        name: '',
        enabled: true,
        sync_interval: 'daily',
        file_filters: ['*.pdf', '*.docx', '*.txt', '*.pptx', '*.xlsx'],
        max_file_size: 50 * 1024 * 1024,
        folder_path: '/',
        share_url: '',
      });
    }
    setActiveStep(0);
    setParsedLinkInfo(null);
    setFileTree(null);
    setShowFileTree(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConnection(null);
    setActiveStep(0);
    setParsedLinkInfo(null);
    setFileTree(null);
    setShowFileTree(false);
  };

  const handleParseSharePointLink = async () => {
    if (!formData.share_url.trim()) {
      showWarning(t('documentSources.warnings.enterSharepointLink'));
      return;
    }

    try {
      const response = await parseSharePointLink.mutateAsync({
        sharepoint_url: formData.share_url,
      });

      if (response.is_valid) {
        setParsedLinkInfo(response);
        setActiveStep(2); // Move to file selection step
        showSuccess(t('documentSources.sharepoint.linkParsedSuccess'));
      } else {
        showError(t('documentSources.sharepoint.invalidLinkFormat'));
      }
    } catch (error) {
      log.error('Error parsing SharePoint link', { error });
      showError(t('documentSources.sharepoint.parseFailed'));
    }
  };

  const handleBrowseFiles = async () => {
    if (!editingConnection) {
      showWarning(t('documentSources.warnings.saveConnectionFirst'));
      return;
    }

    try {
      const response = await browseSharePointFiles.mutateAsync({
        connectionId: editingConnection.id,
        params: {
          folder_path: parsedLinkInfo?.folder_path || '/',
          max_depth: 3,
        },
      });

      setFileTree(response.file_tree);
      setShowFileTree(true);
      showSuccess(t('documentSources.fileSelection.itemsFound', { count: response.total_nodes }));
    } catch (error) {
      log.error('Error browsing files', { error });
      showError(t('documentSources.messages.loadFailed'));
    }
  };

  const handleSaveConnection = async () => {
    try {
      if (editingConnection) {
        const updatedConnection = await updateConnection.mutateAsync({
          connectionId: editingConnection.id,
          data: formData,
        });
        showSuccess(t('documentSources.messages.saveSuccess'));
        setEditingConnection(updatedConnection);
      } else {
        const newConnection = await createConnection.mutateAsync(formData);
        showSuccess(t('documentSources.messages.saveSuccess'));
        setEditingConnection(newConnection);
      }

      // If we have a SharePoint link, move to file selection
      if (formData.share_url && activeStep === 0) {
        setActiveStep(1);
      }
    } catch (error) {
      log.error('Error saving connection', { error });
      showError(t('documentSources.messages.saveFailed'));
    }
  };

  const handleSyncConnection = async (connection: DocumentSourceUserConnectionResponse) => {
    try {
      await syncConnection.mutateAsync(connection.id);
      showSuccess(t('documentSources.sync.syncSuccess'));
    } catch (error) {
      log.error('Error starting sync', { error });
      showError(t('documentSources.sync.syncFailed'));
    }
  };

  const handleSyncSelected = async () => {
    if (!editingConnection || !fileTree) {
      return;
    }

    try {
      await syncSelectedFiles.mutateAsync({
        connectionId: editingConnection.id,
        params: {
          selected_tree: fileTree,
          sync_mode: 'selected',
        },
      });
      showSuccess(t('documentSources.sync.selectiveSyncSuccess'));
      handleCloseDialog();
    } catch (error) {
      log.error('Error starting selective sync', { error });
      showError(t('documentSources.sync.selectiveSyncFailed'));
    }
  };

  const handleDeleteConnection = async () => {
    if (!connectionToDelete) {return;}

    try {
      await deleteConnection.mutateAsync(connectionToDelete.id);
      showSuccess(t('documentSources.messages.deleteSuccess'));
      setDeleteConfirmOpen(false);
      setConnectionToDelete(null);
    } catch (error) {
      log.error('Error deleting connection', { error });
      showError(t('documentSources.messages.deleteFailed'));
    }
  };

  const renderFileTree = (node: FileTreeNode): React.ReactNode => {
    return (
      <TreeItem
        key={node.id}
        nodeId={node.id}
        label={
          <Box display="flex" alignItems="center">
            {node.is_file ? <FileIcon sx={{ mr: 1 }} /> : <FolderIcon sx={{ mr: 1 }} />}
            <Typography variant="body2">
              {node.name}
              {node.is_file && node.size && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({(node.size / 1024 / 1024).toFixed(1)}MB)
                </Typography>
              )}
            </Typography>
          </Box>
        }
      >
        {node.children?.map((child) => renderFileTree(child))}
      </TreeItem>
    );
  };

  // Show loading state or error
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (connectionsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('documentSources.messages.loadFailed', 'Failed to load document source connections')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('documentSources.connections.title', 'My Document Sources')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('documentSources.connections.addConnection', 'Add Connection')}
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        {connections.length === 0 ? (
          <Alert severity="info">
            {t('documentSources.connections.noConnections', 'No document source connections configured yet.')}
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {connections.map((connection) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={connection.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ mr: 2 }}>
                        {getProviderIcon(connection.provider_type)}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Typography variant="h6">{connection.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {connection.provider_name}
                        </Typography>
                      </Box>
                      <Chip
                        label={connection.status}
                        color={getStatusColor(connection.status) as any}
                        size="small"
                      />
                    </Box>

                    {connection.share_url && (
                      <Box display="flex" alignItems="center" mb={1}>
                        <LinkIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="caption" color="text.secondary">
                          {t('documentSources.sharepoint.linkConnected')}
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {t('documentSources.dialog.path')}: {connection.folder_path}
                    </Typography>

                    {connection.last_sync_at && (
                      <Typography variant="caption" color="text.secondary">
                        {t('documentSources.dialog.lastSync')}: {formatDateTime(connection.last_sync_at, i18n.language)}
                      </Typography>
                    )}

                    {connection.error_count > 0 && connection.last_error && (
                      <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        {connection.last_error}
                      </Alert>
                    )}
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(connection)}
                      color="primary"
                      title={t('documentSources.editConnection')}
                      aria-label={t('documentSources.editConnection')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleSyncConnection(connection)}
                      color="info"
                      disabled={syncConnection.isPending}
                      title={t('documentSources.syncConnection')}
                      aria-label={t('documentSources.syncConnection')}
                    >
                      {syncConnection.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConnectionToDelete(connection);
                        setDeleteConfirmOpen(true);
                      }}
                      color="error"
                      title={t('documentSources.deleteConnection')}
                      aria-label={t('documentSources.deleteConnection')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConnection
            ? t('documentSources.connections.editConnection', 'Edit Connection')
            : t('documentSources.connections.addConnection', 'Add Connection')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Basic Configuration */}
            {activeStep === 0 && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t('documentSources.dialog.provider')}</InputLabel>
                    <Select
                      value={formData.provider_config_id}
                      onChange={(e) =>
                        setFormData({ ...formData, provider_config_id: e.target.value })
                      }
                      required
                    >
                      {providerConfigs.map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          {config.name} ({config.provider_type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('documentSources.dialog.connectionName')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    helperText={t('documentSources.dialog.connectionNameHelp')}
                  />
                </Grid>

                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled}
                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                      />
                    }
                    label={t('documentSources.dialog.enabled')}
                  />
                </Grid>
              </Grid>
            )}

            {/* Step 2: SharePoint Link */}
            {activeStep === 1 && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('documentSources.steps.sharepointLink')}
                    value={formData.share_url}
                    onChange={(e) => setFormData({ ...formData, share_url: e.target.value })}
                    multiline
                    rows={3}
                    placeholder={t('documentSources.sharepoint.placeholder')}
                    helperText={isPublicLink
                      ? t('documentSources.sharepoint.publicLinkHelp')
                      : t('documentSources.sharepoint.linkHelp')
                    }
                  />
                </Grid>

                <Grid size={12}>
                  <Button
                    variant="outlined"
                    onClick={handleParseSharePointLink}
                    startIcon={<InfoIcon />}
                    disabled={!formData.share_url.trim() || parseSharePointLink.isPending}
                  >
                    {parseSharePointLink.isPending ? t('documentSources.sharepoint.parsing') : t('documentSources.sharepoint.parseLink')}
                  </Button>
                </Grid>

                {parsedLinkInfo && (
                  <Grid size={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>{t('documentSources.sharepoint.parsedLinkInfo')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Typography variant="body2">
                              <strong>{t('documentSources.sharepoint.domain')}:</strong> {parsedLinkInfo.tenant_domain}
                            </Typography>
                          </Grid>
                          <Grid size={12}>
                            <Typography variant="body2">
                              <strong>{t('documentSources.sharepoint.user')}:</strong> {parsedLinkInfo.user_principal}
                            </Typography>
                          </Grid>
                          <Grid size={12}>
                            <Typography variant="body2">
                              <strong>{t('documentSources.sharepoint.folder')}:</strong> {parsedLinkInfo.folder_path}
                            </Typography>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Step 3: File Selection */}
            {activeStep === 2 && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t('documentSources.fileSelection.title')}</Typography>
                    <Button
                      variant="outlined"
                      onClick={handleBrowseFiles}
                      startIcon={<VisibilityIcon />}
                      disabled={!editingConnection || browseSharePointFiles.isPending}
                    >
                      {browseSharePointFiles.isPending ? t('documentSources.fileSelection.browsing') : t('documentSources.fileSelection.browseFiles')}
                    </Button>
                  </Box>
                </Grid>

                {showFileTree && fileTree && (
                  <Grid size={12}>
                    <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('documentSources.fileSelection.availableFiles')}
                      </Typography>
                      <SimpleTreeView
                        defaultCollapseIcon={<ExpandMoreIcon />}
                        defaultExpandIcon={<ExpandMoreIcon style={{ transform: 'rotate(-90deg)' }} />}
                      >
                        {renderFileTree(fileTree)}
                      </SimpleTreeView>
                    </Paper>
                  </Grid>
                )}

                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t('documentSources.fileSelection.description')}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('documentSources.dialog.cancel')}</Button>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(activeStep - 1)}>{t('documentSources.dialog.back')}</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={activeStep === 0 ? handleSaveConnection : () => setActiveStep(activeStep + 1)}
              variant="contained"
              color="primary"
            >
              {activeStep === 0 ? t('documentSources.dialog.saveAndContinue') : t('documentSources.dialog.next')}
            </Button>
          ) : (
            <Button
              onClick={handleSyncSelected}
              variant="contained"
              color="primary"
              disabled={syncSelectedFiles.isPending}
            >
              {syncSelectedFiles.isPending ? t('documentSources.sync.startingSync') : t('documentSources.sync.startSync')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('documentSources.dialog.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('documentSources.dialog.deleteConfirmMessage', { name: connectionToDelete?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('documentSources.dialog.cancel')}</Button>
          <Button onClick={handleDeleteConnection} color="error" variant="contained">
            {t('documentSources.dialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentSourceConnections;